import { defineStore } from 'pinia';
import { fetchWrapper } from '@/helpers';

const baseUrl = `${process.env.VUE_APP_VUE_API_URL}`;

function normalizeMessage(message) {
    if (!message) return null;
    return {
        ...message,
        from: message.from ?? message.sender_id,
        to: message.to ?? message.receiver_id,
        text: message.text ?? message.content ?? '',
        timestamp: message.timestamp ?? message.createdAt,
        read: Boolean(message.read ?? message.is_read),
        image_url: message.image_url ? baseUrl.split('/api_v1')[0] + message.image_url : null,   // signed, expiring link on the backend origin
        file_url: message.file_url ? baseUrl.split('/api_v1')[0] + message.file_url : null,      // same for other files (txt, ovpn, pdf ...)
        has_image: Boolean(message.has_image),
        reactions: Array.isArray(message.reactions) ? message.reactions : [],   // [{ emoji, count, mine }]
        pinned: Boolean(message.pinned ?? message.is_pinned),
        deleted: Boolean(message.deleted ?? message.deleted_at),
        edited: Boolean(message.edited ?? message.edited_at),
        source: message.source || 'web',
        replyToId: message.replyToId ?? message.reply_to_id ?? null,
        replyTo: message.replyTo || null,
    };
}
// a changed row (edit, reaction, read mark, pin, delete) replaces its old copy; a new one is appended in id order
function mergeMessages(current, incoming) {
    const byId = new Map(current.map((m) => [m.id, m]));
    let added = 0;
    for (const m of incoming) { if (!byId.has(m.id)) added++; byId.set(m.id, m); }
    return { list: [...byId.values()].sort((a, b) => a.id - b.id), added };
}

function ignoreSilent(error) {
    if (error?.silent) return true;
    return false;
}

export const useChatStore = defineStore('chat', {
    state: () => ({
        messages: [],
        users: [],
        unreadCount: 0,
        loadingMessages: false,
        loadingUsers: false,
        hasMoreMessages: false,
        messageOffset: 0,
        messageLimit: 50,
        activeUserId: null,
        serverTime: null,      // the server clock at the last page/poll: changedSince for the next poll
        replyTo: null,         // message being answered (quote above the composer)
        quickReplies: [],
        quickRepliesLoaded: false,
    }),
    actions: {
        clear() {
            this.messages = [];
            this.users = [];
            this.unreadCount = 0;
            this.hasMoreMessages = false;
            this.messageOffset = 0;
            this.activeUserId = null;
        },
        async getMessages(userId = null, options = {}) {
            try {
                const reset = options.reset !== false;
                const offset = reset ? 0 : this.messageOffset;
                this.loadingMessages = true;
                const res = await fetchWrapper.post(`${baseUrl}/chat/messages`, {
                    userId,
                    offset,
                    limit: this.messageLimit,
                });
                const messages = (res.messages || []).map(normalizeMessage).filter(Boolean);
                if (res.serverTime) this.serverTime = res.serverTime;
                if (reset) {
                    this.messages = messages;
                    this.messageOffset = messages.length;
                    this.activeUserId = userId || null;
                    this.replyTo = null;
                } else {
                    this.messages = [...messages, ...this.messages];
                    this.messageOffset += messages.length;
                }
                this.hasMoreMessages = Boolean(res.hasMore);
                return this.messages;
            } catch (error) {
                ignoreSilent(error);
                return [];
            } finally {
                this.loadingMessages = false;
            }
        },
        async loadMoreMessages(userId = null) {
            if (!this.hasMoreMessages || this.loadingMessages) return;
            return this.getMessages(userId, { reset: false });
        },
        async refreshMessages(userId = null) {
            if (this.loadingMessages) return [];
            const activeUserId = userId || null;
            if (this.activeUserId !== activeUserId || this.messages.length === 0) {
                return this.getMessages(userId, { reset: true });
            }

            const lastId = this.messages.reduce((maxId, message) => {
                const id = Number(message?.id || 0);
                return id > maxId ? id : maxId;
            }, 0);

            if (!lastId) return this.getMessages(userId, { reset: true });

            try {
                this.loadingMessages = true;
                const res = await fetchWrapper.post(`${baseUrl}/chat/messages`, {
                    userId,
                    afterId: lastId,
                    changedSince: this.serverTime || undefined,
                    limit: this.messageLimit,
                });
                if (res.serverTime) this.serverTime = res.serverTime;
                const incoming = (res.messages || []).map(normalizeMessage).filter(Boolean);
                if (!incoming.length) return [];
                const existingIds = new Set(this.messages.map((message) => message.id));
                const { list, added } = mergeMessages(this.messages, incoming);
                this.messages = list;
                this.messageOffset += added;
                return incoming.filter((message) => !existingIds.has(message.id));
            } catch (error) {
                ignoreSilent(error);
                return [];
            } finally {
                this.loadingMessages = false;
            }
        },
        // picture message: multipart upload, 5 MB max, JPEG/PNG/GIF/WebP
        async uploadImage(file, receiverId = null, caption = "") {
            try {
                const { useAccountStore } = await import("@/stores");
                const token = useAccountStore().user?.accessToken;
                const form = new FormData();
                form.append("image", file);
                if (caption) form.append("caption", caption);
                if (receiverId) form.append("receiverId", receiverId);
                const res = await fetch(`${baseUrl}/chat/upload`, { method: "POST", headers: { "x-access-token": token }, body: form });
                const data = await res.json().catch(() => ({}));
                if (!res.ok) throw new Error(data.message || "Could not send the file");
                const message = normalizeMessage(data.message);
                if (message) this.messages = [...this.messages, message];
                return message;
            } catch (error) {
                const { useAlertStore } = await import("@/stores");
                useAlertStore().error(error?.message || "Could not send the picture");
                return null;
            }
        },
        // admin: remove a whole message (soft: both sides keep a "Message deleted" slot)
        async deleteMessage(messageId) {
            try {
                const res = await fetchWrapper.post(`${baseUrl}/chat/message/delete`, { message_id: messageId });
                const updated = normalizeMessage(res.deleted);
                this.messages = updated ? this.messages.map((m) => (m.id === updated.id ? updated : m)) : this.messages.filter((m) => m.id !== messageId);
                return true;
            } catch (error) {
                const { useAlertStore } = await import("@/stores");
                useAlertStore().error(error?.message || "Could not delete the message");
                return false;
            }
        },
        // admin: remove a picture from a message (the message stays with a note)
        async deleteImage(messageId) {
            try {
                const res = await fetchWrapper.post(`${baseUrl}/chat/image/delete`, { message_id: messageId });
                const updated = normalizeMessage(res.message);
                if (updated) this.messages = this.messages.map((m) => (m.id === updated.id ? updated : m));
                return true;
            } catch (error) {
                const { useAlertStore } = await import("@/stores");
                useAlertStore().error(error?.message || "Could not delete the picture");
                return false;
            }
        },
        async sendMessage(content, receiverId = null) {
            const cleanContent = String(content || '').trim();
            if (!cleanContent) return null;
            try {
                const res = await fetchWrapper.post(`${baseUrl}/chat/send`, {
                    content: cleanContent,
                    receiverId,
                    replyToId: this.replyTo?.id || undefined,
                });
                const message = normalizeMessage(res.message);
                if (message) {
                    this.messages = [...this.messages, message];
                    this.messageOffset += 1;
                    this.replyTo = null;
                }
                return message;
            } catch (error) {
                if (!ignoreSilent(error)) throw error;
                return null;
            }
        },
        replace(updated) {
            const message = normalizeMessage(updated);
            if (message) this.messages = this.messages.map((m) => (m.id === message.id ? message : m));
            return message;
        },
        // admin: edit one of the support replies
        async editMessage(messageId, content) {
            const res = await fetchWrapper.post(`${baseUrl}/chat/message/edit`, { messageId, content });
            return this.replace(res.message);
        },
        // both sides: toggle an emoji on a message (status: true = 👀 looking / ✅ done, exclusive)
        async react(messageId, emoji, { status = false } = {}) {
            const res = await fetchWrapper.post(`${baseUrl}/chat/react`, { messageId, emoji, status });
            return this.replace(res.message);
        },
        async pin(messageId, pinned) {
            const res = await fetchWrapper.post(`${baseUrl}/chat/pin`, { messageId, pinned });
            return this.replace(res.message);
        },
        async pinned(userId = null) {
            const res = await fetchWrapper.post(`${baseUrl}/chat/pinned`, { userId });
            return (res.messages || []).map(normalizeMessage).filter(Boolean);
        },
        // admin: tag a conversation for the next employee (🔁) or clear the tag
        async setPickup(userId, needsPickup, note = '') {
            const res = await fetchWrapper.post(`${baseUrl}/chat/pickup`, { userId, needsPickup, note });
            const t = res.thread || {};
            this.users = this.users.map((u) => String(u.user_id) === String(userId) ? { ...u, needsPickup: t.needs_pickup, needs_pickup: t.needs_pickup, pickupNote: t.pickup_note, pickupBy: t.pickup_by } : u);
            return t;
        },
        setReplyTo(message) { this.replyTo = message || null; },
        // quick replies (admin)
        async loadQuickReplies(force = false) {
            if (this.quickRepliesLoaded && !force) return this.quickReplies;
            try { this.quickReplies = (await fetchWrapper.get(`${baseUrl}/admin/chat/quick-replies`)).quick_replies || []; this.quickRepliesLoaded = true; }
            catch (error) { ignoreSilent(error); }
            return this.quickReplies;
        },
        async saveQuickReply(reply) {
            const res = await fetchWrapper.post(`${baseUrl}/admin/chat/quick-replies`, reply);
            await this.loadQuickReplies(true);
            return res.quick_reply;
        },
        async deleteQuickReply(uuid) {
            await fetchWrapper.post(`${baseUrl}/admin/chat/quick-replies/delete`, { uuid });
            await this.loadQuickReplies(true);
        },
        async messageRead(userId = null) {
            try {
                await fetchWrapper.post(`${baseUrl}/chat/read`, { userId });
                if (userId) {
                    this.users = this.users.map((user) => String(user.user_id) === String(userId) ? { ...user, unreadCount: 0, unread_count: 0 } : user);
                }
                await this.loadUnreadCount({ silent: true });
            } catch (error) {
                ignoreSilent(error);
            }
        },
        async markUnread(userId) {
            try {
                await fetchWrapper.post(`${baseUrl}/chat/unread`, { userId });
                await this.getUsers();
                await this.loadUnreadCount({ silent: true });
            } catch (error) {
                ignoreSilent(error);
            }
        },
        async getUsers() {
            try {
                this.loadingUsers = true;
                const res = await fetchWrapper.get(`${baseUrl}/chat/users`);
                this.users = (res.users || []).map((user) => ({
                    ...user,
                    user_id: user.user_id ?? user.id,
                    unreadCount: Number(user.unreadCount ?? user.unread_count ?? 0),
                    needsPickup: Boolean(user.needsPickup ?? user.needs_pickup),
                }));
                this.unreadCount = this.users.reduce((total, user) => total + Number(user.unreadCount || 0), 0);
                return this.users;
            } catch (error) {
                ignoreSilent(error);
                return [];
            } finally {
                this.loadingUsers = false;
            }
        },
        async loadUnreadCount() {
            // signed out, or a stale session with no token: nothing to ask the server
            const { useAccountStore } = await import("@/stores");
            if (!useAccountStore().user?.accessToken) return this.unreadCount;
            try {
                const res = await fetchWrapper.get(`${baseUrl}/chat/unread-count`);
                this.unreadCount = Number(res.unreadCount || 0);
                return this.unreadCount;
            } catch (error) {
                ignoreSilent(error);
                return 0;
            }
        },
    },
});
